import React, { useEffect } from 'react';
import T from 'i18n';
import {
  MenuItem,
  Menu,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Box,
} from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Delete, Edit, Group } from '@mui/icons-material';
import { groupByDateLabel, groupDataByDate } from 'utils/groupDataByDate';
import { removeFormatSuffix } from './utils/formatUtils';

import { useDeleteSearchHistoryItem } from '../redux/search-history-content/deleteSearchHistoryContentItem';
import { useUpdateSearchHistoryItem } from '../redux/search-history-content/updateSearchHistoryContentItem';
import { useFetchSearchContentHistory } from '../redux/search-history-content/fetchSearchContentHistory';

interface SearchContentHistoryProps {
  open: boolean;
  anchorEl: any;
  handleSearchHistoryClose: any;
  handleSearchItemClick: (item: any) => void;
}

const SearchContentHistory = ({
  open,
  anchorEl,
  handleSearchHistoryClose,
  handleSearchItemClick,
}: SearchContentHistoryProps) => {
  const {
    searchHistory: searchHistoryOriginal,
    fetchSearchHistoryPending,
    fetchSearchContentHistory,
  } = useFetchSearchContentHistory();
  const { updateSearchHistoryItemPending, updateSearchHistoryItem } = useUpdateSearchHistoryItem();
  const { deleteSearchHistoryItemPending, deleteSearchHistoryItem } = useDeleteSearchHistoryItem();
  const [selectedItem, setSelectedItem] = React.useState<any>(null);
  const [optimisticNames, setOptimisticNames] = React.useState<Record<string, string>>({});
  const [searchHistory, setSearchHistory] = React.useState<any>({});
  const [deletingId, setDeletingId] = React.useState<string | number | null>(null);
  const [updatingId, setUpdatingId] = React.useState<string | number | null>(null);

  // Normalize items to the shape expected by groupDataByDate (expects createdBy.date)
  const normalizeForGrouping = (items: any) => {
    if (!Array.isArray(items)) return items;
    return items.map((it: any) => {
      const createdBy = it?.createdBy && typeof it.createdBy === 'object' ? it.createdBy : {};
      const date = createdBy?.date ?? it?.date;
      return { ...it, createdBy: { ...createdBy, date } };
    });
  };

  const updateItem = (searchHistoryItem: any) => {
    if (updatingId) return;

    setOptimisticNames((prev) => ({ ...prev, [searchHistoryItem.id]: searchHistoryItem.name }));

    setUpdatingId(searchHistoryItem.id);
    const maybePromise = updateSearchHistoryItem({ searchHistoryItem });

    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise
        .then(() => fetchSearchContentHistory())
        .catch(() => {})
        .finally(() => {
          setUpdatingId(null);
          setSelectedItem(null);
        });
    } else {
      setUpdatingId(null);
      setSelectedItem(null);
    }
  };

  const showGlobalLoader =
    fetchSearchHistoryPending &&
    (!searchHistoryOriginal ||
      (Array.isArray(searchHistoryOriginal) && searchHistoryOriginal.length === 0));

  const areAllGroupsEmpty = (groups: any) => {
    return Object.values(groups).every((group: any) => group.length === 0);
  };

  const hasOnlyOlderGroup = (groups: any) => {
    if (!groups || typeof groups !== 'object') return false;
    const keys = Object.keys(groups);
    return keys.every((k) => (k === 'older' ? groups[k]?.length > 0 : groups[k]?.length === 0));
  };

  useEffect(() => {
    if (open) {
      fetchSearchContentHistory().then((res: any) => {
        const formattedData = groupDataByDate(normalizeForGrouping(res));
        setSearchHistory(formattedData);
      });
    }
  }, [fetchSearchContentHistory, open]);

  // When the menu closes, cancel edit mode and any local flags
  useEffect(() => {
    if (!open) {
      setSelectedItem(null);
      setDeletingId(null);
      setUpdatingId(null);
    }
  }, [open]);

  useEffect(() => {
    const formattedData = groupDataByDate(normalizeForGrouping(searchHistoryOriginal));
    setSearchHistory(formattedData);
  }, [searchHistoryOriginal]);

  useEffect(() => {
    if (!fetchSearchHistoryPending) {
      setOptimisticNames({});
    }
  }, [fetchSearchHistoryPending]);
  return (
    <Menu
      id="search-history-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleSearchHistoryClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      {showGlobalLoader ? (
        <Box
          sx={{
            width: '30vw',
            maxHeight: '60vh',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : searchHistory && !areAllGroupsEmpty(searchHistory) ? (
        <PerfectScrollbar style={{ maxHeight: '60vh' }}>
          {hasOnlyOlderGroup(searchHistory)
            ? (searchHistory.older || []).map((searchHistoryItm: any, indexV: number) => (
                <MenuItem
                  key={indexV}
                  disableRipple
                  onClick={() => {
                    if (
                      deletingId === searchHistoryItm.id ||
                      deleteSearchHistoryItemPending ||
                      updatingId === searchHistoryItm.id ||
                      updateSearchHistoryItemPending
                    )
                      return;
                    handleSearchItemClick(searchHistoryItm);
                  }}
                  sx={{
                    justifyContent: 'space-between',
                    opacity:
                      deletingId === searchHistoryItm.id || updatingId === searchHistoryItm.id
                        ? 0.6
                        : 1,
                  }}
                >
                  {selectedItem && selectedItem.id === searchHistoryItm.id ? (
                    <TextField
                      autoFocus
                      size="small"
                      defaultValue={removeFormatSuffix(selectedItem.name)}
                      placeholder={T.translate('case.searchHistoryItemPlaceholder')}
                      required
                      disabled={
                        updatingId === searchHistoryItm.id || updateSearchHistoryItemPending
                      }
                      InputProps={{
                        endAdornment: (updatingId === searchHistoryItm.id ||
                          updateSearchHistoryItemPending) && (
                          <InputAdornment position="end">
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ),
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseDown={(e) => {
                        // Prevent parent MenuItem ripple/click from firing
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onKeyDown={(e: any) => {
                        // Stop key events from bubbling to the MenuItem
                        e.stopPropagation();
                        if (
                          e.key === 'Enter' &&
                          e.target.value &&
                          selectedItem.name !== e.target.value
                        ) {
                          updateItem({ ...selectedItem, name: e.target.value });
                        }
                      }}
                      onKeyUp={(e) => {
                        // Also stop bubbling on keyup to avoid accidental parent handling
                        e.stopPropagation();
                      }}
                      onBlur={(e: any) =>
                        e.target.value &&
                        selectedItem.name !== e.target.value &&
                        updateItem({ ...selectedItem, name: e.target.value })
                      }
                      fullWidth
                    />
                  ) : (
                    <>
                      <Typography
                        sx={{
                          width: '25vw',
                          wordWrap: 'break-word',
                          whiteSpace: 'normal',
                          textOverflow: 'clip',
                        }}
                      >
                        {removeFormatSuffix(
                          optimisticNames[searchHistoryItm.id] ?? searchHistoryItm.name,
                        )}
                      </Typography>
                      <div>
                        {searchHistoryItm.contributors &&
                          searchHistoryItm.contributors.length > 0 && (
                            <Tooltip
                              title={searchHistoryItm.contributors
                                .map((itm: any) => itm.name)
                                .join(', ')}
                              placement="top"
                              arrow
                            >
                              <Group
                                fontSize="small"
                                sx={{ cursor: 'default', color: 'GrayText' }}
                              />
                            </Tooltip>
                          )}
                        {deletingId === searchHistoryItm.id ||
                        deleteSearchHistoryItemPending ||
                        fetchSearchHistoryPending ? (
                          <CircularProgress size={16} sx={{ ml: 1 }} />
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedItem(searchHistoryItm);
                              }}
                              disabled={
                                updateSearchHistoryItemPending ||
                                deleteSearchHistoryItemPending ||
                                fetchSearchHistoryPending
                              }
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDeletingId(searchHistoryItm.id);
                                deleteSearchHistoryItem({ searchHistoryItm })
                                  .then(() => fetchSearchContentHistory())
                                  .finally(() => setDeletingId(null));
                                selectedItem && setSelectedItem(null);
                              }}
                              disabled={deleteSearchHistoryItemPending || fetchSearchHistoryPending}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </MenuItem>
              ))
            : Object.entries(searchHistory).map(
                ([key, value]: any, index: number) =>
                  value.length > 0 && (
                    <React.Fragment key={index}>
                      <MenuItem disabled>
                        <Typography variant="subtitle1">{groupByDateLabel(key)}</Typography>
                      </MenuItem>
                      {value.map((searchHistoryItm: any, indexV: number) => (
                        <MenuItem
                          key={indexV}
                          disableRipple
                          onClick={() => {
                            if (
                              deletingId === searchHistoryItm.id ||
                              deleteSearchHistoryItemPending ||
                              updatingId === searchHistoryItm.id ||
                              updateSearchHistoryItemPending ||
                              fetchSearchHistoryPending
                            )
                              return;
                            handleSearchItemClick(searchHistoryItm);
                          }}
                          sx={{
                            justifyContent: 'space-between',
                            opacity:
                              deletingId === searchHistoryItm.id ||
                              updatingId === searchHistoryItm.id
                                ? 0.6
                                : 1,
                          }}
                        >
                          {selectedItem && selectedItem.id === searchHistoryItm.id ? (
                            <TextField
                              autoFocus
                              size="small"
                              defaultValue={removeFormatSuffix(selectedItem.name)}
                              placeholder={T.translate('case.searchHistoryItemPlaceholder')}
                              required
                              disabled={
                                updatingId === searchHistoryItm.id || updateSearchHistoryItemPending
                              }
                              InputProps={{
                                endAdornment: (updatingId === searchHistoryItm.id ||
                                  updateSearchHistoryItemPending) && (
                                  <InputAdornment position="end">
                                    <CircularProgress size={16} />
                                  </InputAdornment>
                                ),
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                // Prevent parent MenuItem ripple/click from firing
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onKeyDown={(e: any) => {
                                // Stop key events from bubbling to the MenuItem
                                e.stopPropagation();
                                if (
                                  e.key === 'Enter' &&
                                  e.target.value &&
                                  selectedItem.name !== e.target.value
                                ) {
                                  updateItem({ ...selectedItem, name: e.target.value });
                                }
                              }}
                              onKeyUp={(e) => {
                                // Also stop bubbling on keyup to avoid accidental parent handling
                                e.stopPropagation();
                              }}
                              onBlur={(e: any) =>
                                e.target.value &&
                                selectedItem.name !== e.target.value &&
                                updateItem({ ...selectedItem, name: e.target.value })
                              }
                              fullWidth
                            />
                          ) : (
                            <>
                              <Typography
                                sx={{
                                  width: '25vw',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  textOverflow: 'clip',
                                }}
                              >
                                {removeFormatSuffix(
                                  optimisticNames[searchHistoryItm.id] ?? searchHistoryItm.name,
                                )}
                              </Typography>
                              <div>
                                {searchHistoryItm.contributors &&
                                  searchHistoryItm.contributors.length > 0 && (
                                    <Tooltip
                                      title={searchHistoryItm.contributors
                                        .map((itm: any) => itm.name)
                                        .join(', ')}
                                      placement="top"
                                      arrow
                                    >
                                      <Group
                                        fontSize="small"
                                        sx={{ cursor: 'default', color: 'GrayText' }}
                                      />
                                    </Tooltip>
                                  )}
                                {deletingId === searchHistoryItm.id ||
                                deleteSearchHistoryItemPending ||
                                fetchSearchHistoryPending ? (
                                  <CircularProgress size={16} sx={{ ml: 1 }} />
                                ) : (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedItem(searchHistoryItm);
                                      }}
                                      disabled={
                                        updateSearchHistoryItemPending ||
                                        deleteSearchHistoryItemPending ||
                                        fetchSearchHistoryPending
                                      }
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setDeletingId(searchHistoryItm.id);
                                        deleteSearchHistoryItem({ searchHistoryItm })
                                          .then(() => fetchSearchContentHistory())
                                          .finally(() => setDeletingId(null));
                                        selectedItem && setSelectedItem(null);
                                      }}
                                      disabled={
                                        deleteSearchHistoryItemPending || fetchSearchHistoryPending
                                      }
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </MenuItem>
                      ))}
                    </React.Fragment>
                  ),
              )}
        </PerfectScrollbar>
      ) : (
        <MenuItem disabled>{T.translate('case.noSearchHistory')}</MenuItem>
      )}
    </Menu>
  );
};

export default SearchContentHistory;
